import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import { useCompletion } from 'ai/react';
import {
  Sparkles,
  ChevronsUp,
  ChevronsDown,
  FileText,
  Play,
  HelpCircle,
  X,
  RotateCcw,
  Trash2,
} from 'lucide-mui';
import ReactMarkdown from 'react-markdown';
import * as MarkdownTypes from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getToken } from '../../../../lib/localStorageHandler';
import config from '../../../../../config';
import FadeStaggerCircles from '../../../../assets/icons/fade-stagger-circles.svg';
import MarkdownComponents from './components/MarkdownComponents';
import { onClickTimestamp } from '../../../../lib/commonUtils';
import {
  saveEnhancedNotesThunk,
  saveNotesThunk,
} from '../../selectedVideoSlice';
import { RootState } from '../../../../reducers';

// Define the props interface for the blockquote component
interface BlockquoteProps extends React.HTMLAttributes<HTMLQuoteElement> {
  children?: React.ReactNode;
}

// Define custom components type
type CustomComponents = Partial<MarkdownTypes.Components> & {
  blockquote: React.ComponentType<BlockquoteProps>;
};

const formatTimestamp = (timestamp: string) => {
  const cleanTimestamp = timestamp.replace(/[[\]()\s]/g, '');
  const timeParts = cleanTimestamp.split(':').map(Number);

  if (timeParts.length === 2) {
    const [minutes, seconds] = timeParts;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`;
  }
  if (timeParts.length === 3) {
    const [hours, minutes, seconds] = timeParts;
    if (hours === 0) {
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}:${String(seconds).padStart(2, '0')}`;
  }

  return timestamp;
};

const convertTimestampToSeconds = (timestamp: string): number => {
  const cleanTimestamp = timestamp.replace(/[[\]()\s]/g, '');
  const timeParts = cleanTimestamp.split(':').map(Number);

  if (timeParts.length === 2) {
    const [minutes, seconds] = timeParts;
    return minutes * 60 + seconds;
  }
  if (timeParts.length === 3) {
    const [hours, minutes, seconds] = timeParts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
};

const Notes: React.FC = () => {
  const dispatch = useDispatch();
  const {
    file,
    transcript,
    postProcess: { systemPromptResponses = {} } = {},
  } = useSelector((state: RootState) => state.selectedVideo);

  const { complete, completion: aiResponse, isLoading, error } = useCompletion({
    api: `${config.authBaseUrlV2}/proxy/ai/completion`,
    streamProtocol: 'text',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    initialCompletion: systemPromptResponses.ENHANCED_NOTES?.responseText,
    onFinish: async (prompt: string, completion: string) => {
      if (file?._id) {
        await dispatch(
          saveEnhancedNotesThunk({
            fileId: file._id,
            responseText: completion,
          })
        );
      }
    },
  });

  const [hideQuotes, setHideQuotes] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [showRecorderInfoDialog, setShowRecorderInfoDialog] = useState(false);
  const [showEnhancedInfoDialog, setShowEnhancedInfoDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  const theme = useTheme();

  const secondsToTimestamp = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return [hours, minutes, remainingSeconds]
      .map((n) => n.toString().padStart(2, '0'))
      .join(':');
  };

  const optimizeNotes = async () => {
    if (!transcript?.compactSegments?.length) {
      return;
    }

    const notes = file?.textData?.notes || [];
    const segments = transcript?.compactSegments || [];

    const formattedNotes = notes.map(
      (note) => `[${secondsToTimestamp(note.timestamp)}] ${note.text}`
    );
    const formattedSegments = segments.map(
      (seg) =>
        `[${secondsToTimestamp(seg.start)}-${secondsToTimestamp(seg.end)}] ${
          seg.speaker
        }: ${seg.text}`
    );

    const prompt = `<?xml version="1.0" encoding="UTF-8"?>
<prompt>
  <directions>
    <role>You are an intelligent note enhancement assistant. Your role is to expand, enhance, and organize the user's original notes by adding relevant context from the transcript.</role>
    
    <input>
      <notes>${formattedNotes.join('\n')}</notes>
      <content_transcript>${formattedSegments.join('\n')}</content_transcript>
    </input>

    <instructions>
      <foundation>Use the user's notes as starting points for more detailed notes with full context</foundation>
      
      <enhancement_guidelines>
        <item>ALWAYS ensure proper capitalization in ALL output:</item>
        <item>- Capitalize the first letter of every sentence</item>
        <item>- Capitalize proper nouns, names, brands, and titles</item>
        <item>- Capitalize acronyms and initialisms</item>
        <item>- Capitalize the first word after a colon in titles</item>
        <item>- When preserving text from original notes, fix any incorrect capitalization</item>
        <item>- If a word is incorrectly capitalized in the middle of a sentence (e.g., "IMpressive"), fix it to be lowercase unless it's a proper noun</item>
        <item>- If a proper noun is not capitalized in the original notes, capitalize it in the enhanced version</item>
        <item>- For titles and headings, use title case (capitalize important words)</item>
        <item>- For technical terms, preserve their standard capitalization (e.g., "iOS", "macOS", "TypeScript")</item>
        <item>- When quoting from the transcript, preserve the speaker's exact words but fix any obvious capitalization errors</item>
        <item>- When merging multiple notes, ensure consistent capitalization across the merged content</item>
        <item>- If there are conflicting capitalizations of the same term in different notes, use the most correct form</item>
        <item>Remove timestamps from notes when enhancing them</item>
        <item>Keep the original note text but remove any [XXs] timestamp prefixes</item>
        <item>Expand brief or incomplete notes using context from the transcript</item>
        <item>Transform short phrases into complete sentences</item>
        <item>Fix any spelling or punctuation errors in the original notes. Inclusive of missing commas, periods, apostrophes, etc.</item>
        <item>Add appropriate punctuation marks where missing</item>
        <item>Add specific numbers, metrics, and details from the surrounding transcript context</item>
        <item>If a note is very brief (like "100, growing"), look for related context in the transcript to provide full details (like "100 employees currently, growing by 20 per quarter")</item>
        <item>Include relevant quotes that support or expand upon the note</item>
        <item>Sometimes there are questions in the notes, answer them in the enhanced output AND its own section</item>
        <item>Sometimes there are action items in the notes, add them to the enhanced output AND its own section</item>
        <item>Bias the corrected original note to be at the start of the output</item>
        <item>Remove redundancies and consolidate similar points:</item>
        <item>- If multiple notes express the same idea, combine them into one comprehensive point</item>
        <item>- When similar information appears in different notes, merge them while preserving all unique details</item>
        <item>- Keep the most detailed or well-expressed version of duplicate information</item>
        <item>- Ensure the consolidated point appears in chronological order based on the earliest mention</item>
        <item>- Example: "SBF romanced the Clintons" and "He was friends with Bill and Hillary Clinton" should become one point about his Clinton connections</item>
        <item>Integrate original notes seamlessly:</item>
        <item>- Bold the key terms/phrases from original notes within the enhanced text</item>
        <item>- Don't repeat the original note separately if it's fully integrated</item>
        <item>- Example: "One of Pharma Bro's investors had also invested with **Soros** and considered Pharma Bro's fund the second best he'd invested in." (instead of "**Soros?** One of...")</item>
        <item>- Remove question marks from original notes when integrating them into statements</item>
        <item>- Keep important terms and phrases from original notes but integrate them naturally</item>
        <item>- If the original note was a question, answer it directly while incorporating the key terms</item>
        <item>Expand and polish incomplete questions:</item>
        <item>- Convert brief question notes (e.g., "advice?") into complete questions</item>
        <item>- Use context from the transcript to form the full question</item>
        <item>- Example: "advice?" should become "What advice did Pharma Bro give to SBF?"</item>
        <item>- Maintain the original keyword but expand it into a proper question</item>
        <item>- If multiple interpretations are possible, use the most relevant one based on context</item>
      </enhancement_guidelines>

      <note_cleaning_rules>
        <rule>Handle incomplete or nonsensical notes:</rule>
        <item>Remove notes that are clearly test entries (e.g., "test", "testing", "123")</item>
        <item>Remove empty notes or notes containing only spaces/punctuation</item>
        <item>Remove notes that are single letters or random characters</item>
        <item>For incomplete words, attempt to reconstruct based on transcript context</item>
        <item>Example: "proj upd" could become "project update" if context supports it</item>
        <item>If an incomplete note's meaning cannot be determined from context, remove it</item>
        <item>If a note appears to be a typing mistake or accident, remove it</item>
        <item>For ambiguous abbreviations, use transcript context to expand correctly</item>
        <item>When multiple interpretations are possible, choose the one best supported by transcript context</item>
        <item>If a note is completely unrelated to the meeting content, remove it</item>
      </note_cleaning_rules>

      <categorization_rules>
        <rule>Analyze notes to identify common themes and topics</rule>
        <rule>Group related notes under appropriate category headings</rule>
        <rule>Common categories may include but are not limited to:</rule>
        <categories>
          - Project Updates
          - Technical Discussion
          - Business Strategy
          - Product Features
          - Customer Feedback
          - Team Updates
          - Marketing Plans
          - Sales Discussion
          - Financial Updates
          - Action Items
          - Decisions Made
          - Problems & Solutions
          - Research Findings
          - Future Plans
        </categories>
        <rule>Create custom categories when needed based on content</rule>
        <rule>Use clear, concise category titles</rule>
        <rule>Maintain chronological order within categories</rule>
        <rule>Notes that span multiple categories should be split or cross-referenced</rule>
      </categorization_rules>

      <output_structure>
        <section id="summary">
          <heading>Content Summary</heading>
        </section>

        <section id="categorized_notes">
          <heading>[Category Name]</heading>
          <format>- _[Context and lead-in]_ **[key terms from original notes]** _[additional context and details]_</format>
          <format>> [HH:MM:SS] "Quote from transcript" - Speaker</format>
        </section>

        <section id="key_points">
          <heading>Key Points</heading>
          <format>- _[Context and lead-in]_ **[key terms from original notes]** _[additional context and details]_</format>
          <format>> [HH:MM:SS] "Quote from transcript" - Speaker</format>
          <note>Integrate original notes naturally into the enhanced text, using bold for key terms</note>
          <example>- One of Pharma Bro's investors had also invested with **Soros** and considered Pharma Bro's fund the second best he'd invested in.</example>
        </section>

        <section id="questions" condition="if_exists">
          <heading>Questions Raised</heading>
          <format>#### [Expanded complete question]</format>
          <format>[Comprehensive answer derived from context]</format>
          <format>> [HH:MM:SS] "Brief, focused quote" - Speaker</format>
          <examples>
            <example>
              #### Did Pharma Bro's investors lose money?
              Despite facing charges of defrauding investors, Pharma Bro asserts that none of his investors experienced financial losses. He highlights that they earned significant returns, with one investor claiming a 30-40% annual profit, making it the second-best hedge fund they had invested in.
              > [00:15:23] "How can you defraud someone who makes 30 or 40% a year?" - Pharma Bro
            </example>
            <example>
              #### What advice did Pharma Bro give to Sam Bankman-Fried?
              Pharma Bro advised SBF to rebrand his image by learning rap music and slang. He specifically suggested creating a new backstory where SBF would present himself as "Sam Bankman from Oakland" rather than maintaining his businessman persona.
              > [00:08:45] "I think you should invent a backstory for yourself where you're not Sam Bankman the businessman but you were Sam Bankman from Oakland" - Pharma Bro
            </example>
          </examples>
        </section>

        <section id="action_items" condition="if_exists">
          <heading>Action Items</heading>
          <format>- [Action item] (**Owner:** [Name if mentioned])</format>
          <format>> [HH:MM:SS] "Supporting quote" - Speaker</format>
        </section>

        <section id="follow_ups" condition="if_exists">
          <heading>Follow-up Topics</heading>
          <format>- [Topic that needs further discussion/clarification]</format>
          <format>> [HH:MM:SS] "Supporting quote" - Speaker</format>
        </section>

        <section id="decisions" condition="if_exists">
          <heading>Key Decisions</heading>
          <format>- [Decision made] (**Context:** [Supporting details])</format>
          <format>> [HH:MM:SS] "Supporting quote" - Speaker</format>
        </section>

        <section id="deadlines" condition="if_exists">
          <heading>Timeline & Deadlines</heading>
          <format>- [Date/Deadline] : [Associated task/milestone]</format>
          <format>> [HH:MM:SS] "Supporting quote" - Speaker</format>
        </section>
      </output_structure>

      <formatting>
        <rule>Use > [HH:MM:SS] "Quote" - Speaker format for all quotes</rule>
        <rule>Format all timestamps as either [MM:SS] or [HH:MM:SS]</rule>
        <rule>For timestamps less than 1 hour, use [MM:SS] format</rule>
        <rule>For timestamps 1 hour or longer, use [HH:MM:SS] format</rule>
        <rule>Always use two digits for each time unit (e.g., [05:08] not [5:8])</rule>
        <rule>Never use any other timestamp format (e.g., no [M:SS] or [H:MM:SS])</rule>
        <rule>Validate all timestamps according to these rules:</rule>
        <rule>- Hours (HH) must be between 00-99</rule>
        <rule>- Minutes (MM) must be between 00-59</rule>
        <rule>- Seconds (SS) must be between 00-59</rule>
        <rule>- No decimal points or fractions allowed</rule>
        <rule>- No negative values allowed</rule>
        <rule>- No special characters except [] and : allowed</rule>
        <rule>Examples of valid timestamps:</rule>
        <rule>- [05:30] - 5 minutes 30 seconds</rule>
        <rule>- [15:45] - 15 minutes 45 seconds</rule>
        <rule>- [01:23:45] - 1 hour 23 minutes 45 seconds</rule>
        <rule>Examples of invalid timestamps:</rule>
        <rule>- [5:30] - Missing leading zero</rule>
        <rule>- [15:60] - Minutes > 59</rule>
        <rule>- [01:23:60] - Seconds > 59</rule>
        <rule>- [1:23:45] - Missing leading zero</rule>
        <rule>Only include timestamps inside quote blocks</rule>
        <rule>Always wrap quotes in quotation marks</rule>
        <rule>Always attribute speaker after quote with - Speaker</rule>
        <rule>Use speaker names instead of IDs when available</rule>
        <rule>Fix transcript issues in quotes:</rule>
        <rule>- Correct obvious spelling errors</rule>
        <rule>- Fix grammatical mistakes</rule>
        <rule>- Add missing words in [brackets] for clarity</rule>
        <rule>- Use [sic] to indicate when preserving an intentional error or unusual phrasing</rule>
        <rule>- Remove speech disfluencies (um, uh, etc) unless meaningful</rule>
        <rule>- Fix capitalization and punctuation</rule>
        <rule>- Maintain original meaning while improving readability</rule>
        <rule>Bold ONLY terms and phrases that appear verbatim in the original notes</rule>
        <rule>Do not bold any terms that were not explicitly written in the original notes</rule>
        <rule>When integrating original notes, only bold the exact words/phrases from those notes</rule>
        <rule>Do not bold related terms, synonyms, or contextual additions</rule>
        <rule>Example: If original note is "meeting with John", only bold "meeting" and "John", not related terms like "discussion" or "appointment"</rule>
        <rule>When in doubt, do not bold - only bold when there is a direct match with original notes</rule>
        <rule>Integrate original notes seamlessly into enhanced text</rule>
        <rule>Remove question marks when converting questions into statements</rule>
        <rule>Always expand brief questions into complete, grammatically correct questions</rule>
        <rule>Include the original brief question as a reference</rule>
        <rule>Questions should be specific and directly related to the content</rule>
        <rule>Format questions as clean Q&A without referencing original notes</rule>
        <rule>Questions should be complete and clear without needing context from original notes</rule>
        <rule>Answers should be comprehensive and self-contained</rule>
        <rule>Keep quotes extremely concise and focused:</rule>
        <rule>- Use only 1 sentence per quote whenever possible</rule>
        <rule>- Maximum length should be 2 short sentences if absolutely necessary</rule>
        <rule>- Select only the most impactful part of longer statements</rule>
        <rule>- Focus on the key message or insight</rule>
        <rule>- Remove any filler words or unnecessary context</rule>
        <rule>- Break longer quotes into separate, focused quotes if needed</rule>
        <rule>- Prioritize quotes that directly support the point being made</rule>
        <rule>- Avoid redundant information in quotes</rule>
        <rule>- If a point can be made with a shorter quote, use it</rule>
      </formatting>
    </instructions>

    <reminders>
      <item>Preserve the user's perspective and priorities</item>
      <item>Add value through context and connections</item>
      <item>Be concise and focused</item>
      <item>Make it easy to distinguish original notes from AI additions</item>
      <item>Use proper names for speakers when available</item>
      <item>Each timestamp at the start of a note is when the user finished the last note and pressed enter</item>
      <item>If a note is empty, it means the user didn't finish the note and pressed enter</item>
      <item>ALWAYS format quotes as > [HH:MM:SS] "Quote" - Speaker</item>
      <item>Consolidate similar points to avoid redundancy</item>
      <item>Keep the most comprehensive version when merging similar notes</item>
    </reminders>
  </directions>

  <rules>
    <item>ALWAYS capitalize properly - no exceptions, especially in the original notes</item>
    <item>Use Markdown to format your response with hyphens (-) for all list items</item>
    <item>Jump right into the content without any preface</item>
    <item>Do not have a closing summary</item>
    <item>Do not end with a signature</item>
    <item>Ensure no significant parts of the content are missed</item>
    <item>Use names when you can to identify speakers. Do not use generic labels like SPEAKER_001</item>
    <item>Do not refer to "transcript" in your response</item>
    <item>Fix any transcript issues you notice</item>
    <item>Never use bullet points (•), always use hyphens (-)</item>
    <item>Every sentence must start with a capital letter</item>
    <item>ALWAYS format quotes as > [HH:MM:SS] "Quote" - Speaker</item>
  </rules>

  <metadata>
    <speakermap>${JSON.stringify(file?.textData?.speakers || {})}</speakermap>
    <customvocab></customvocab>
    <transcript>${JSON.stringify(segments)}</transcript>
  </metadata>
</prompt>`;

    console.log('Sending prompt to AI:', prompt);

    try {
      const response = await complete(prompt, {
        body: {
          model: 'better',
          stream: true,
          feature: 'notes',
          args: {
            fileId: file?._id,
            teamId: file?.teamId,
            ownerId: file?.ownerId,
          },
        },
      });

      console.log('Complete Response:', response);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleNoteEdit = async (index: number, newText: string) => {
    const notes = file?.textData?.notes || [];
    const updatedNotes = [...notes];
    updatedNotes[index] = {
      ...updatedNotes[index],
      text: newText,
    };

    if (file?._id) {
      await dispatch(
        saveNotesThunk({
          fileId: file._id,
          notes: updatedNotes,
        })
      );
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setNoteToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteNote = async () => {
    if (noteToDelete === null) return;

    const notes = file?.textData?.notes || [];
    const updatedNotes = notes.filter((_, i) => i !== noteToDelete);

    if (file?._id) {
      await dispatch(
        saveNotesThunk({
          fileId: file._id,
          notes: updatedNotes,
        })
      );
    }
    setDeleteConfirmOpen(false);
    setNoteToDelete(null);
  };

  const renderOriginalNotes = () => {
    const notes = file?.textData?.notes || [];

    return (
      <Box sx={{ mt: 2 }}>
        {notes.map((note, index) => (
          <Paper
            key={index}
            elevation={0}
            onClick={() => onClickTimestamp(note.timestamp)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: (t) => t.spacing(0.75, 1.5),
              cursor: 'pointer',
              backgroundColor: 'transparent',
              transition: (t) =>
                t.transitions.create(['background-color'], {
                  duration: t.transitions.duration.shortest,
                }),
              '&:hover': {
                backgroundColor: 'action.hover',
                '& .timestamp-chip': {
                  opacity: 1,
                  '& .MuiSvgIcon-root': {
                    opacity: 1,
                    transform: 'scale(1)',
                    width: '18px',
                  },
                },
                '& .note-actions': {
                  opacity: 1,
                },
              },
              mb: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleNoteEdit(index, e.currentTarget.textContent || '')
              }
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{
                fontWeight: 600,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-wrap',
                outline: 'none',
                '&:focus': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 1,
                },
              }}
            >
              {note.text}
            </Typography>
            <Box
              className="note-actions"
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              <Tooltip title="Delete note" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteClick(e, index)}
                  sx={{
                    color: 'action.active',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'error.lighter',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Trash2 fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                className="timestamp-chip"
                icon={
                  <Play
                    sx={{
                      fontSize: '18px',
                      opacity: 0,
                      transform: 'scale(0.8)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      mr: '-8px',
                      color: 'primary.main',
                      width: 0,
                      overflow: 'hidden',
                    }}
                  />
                }
                label={secondsToTimestamp(note.timestamp).replace(/^00:/, '')}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickTimestamp(note.timestamp);
                }}
                clickable
                sx={{
                  cursor: 'pointer',
                  backgroundColor: 'background.paper',
                  color: 'primary.main',
                  height: '24px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'auto',
                  border: '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'background.paper',
                    opacity: '1 !important',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    '& .MuiSvgIcon-root': {
                      opacity: 1,
                      transform: 'scale(1)',
                      width: '18px',
                      color: 'primary.main',
                    },
                  },
                  '& .MuiChip-label': {
                    padding: '0 8px',
                    paddingLeft: '4px !important',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    lineHeight: 1,
                  },
                  '& .MuiChip-icon': {
                    ml: 0,
                    mr: 0,
                    pl: 0.5,
                    transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: 0,
                  },
                }}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  // Move processTimestamps function before customComponents definition
  const processTimestamps = (content: string) => {
    const processedContent = [];
    let currentIndex = 0;
    const timestampRegex = /\[(?:(\d{2}):)?(\d{2}):(\d{2})\]/g;
    let match = timestampRegex.exec(content);

    while (match) {
      // Add text before the timestamp
      if (match.index > currentIndex) {
        processedContent.push(content.slice(currentIndex, match.index));
      }

      // Add the timestamp as a Chip
      const timestamp = match[0];
      const seconds = convertTimestampToSeconds(timestamp);
      const displayTimestamp = formatTimestamp(timestamp);

      processedContent.push(
        <Chip
          key={`timestamp-${match.index}`}
          label={displayTimestamp}
          onClick={() => onClickTimestamp(seconds)}
          clickable
          sx={{
            cursor: 'pointer',
            backgroundColor: 'transparent',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiChip-label': {
              padding: '0 4px',
              color: 'primary.main',
              fontSize: '0.875rem',
            },
            verticalAlign: 'baseline',
            height: '20px',
            marginTop: '-2px',
          }}
        />
      );

      currentIndex = timestampRegex.lastIndex;
      match = timestampRegex.exec(content);
    }

    // Add any remaining text
    if (currentIndex < content.length) {
      processedContent.push(content.slice(currentIndex));
    }

    return processedContent;
  };

  const customComponents: CustomComponents = {
    ...MarkdownComponents,
    blockquote: ({ children, ...props }: BlockquoteProps) => {
      const blockquoteStyle: React.CSSProperties = {
        display: hideQuotes ? 'none' : 'block',
        borderLeft: `4px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(2),
        margin: `${theme.spacing(1)} 0`,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        background: theme.palette.background.paper,
        borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
        marginLeft: 0,
        marginRight: 0,
      };

      // Process the content to handle both timestamps and other elements
      const processContent = (child: React.ReactNode): React.ReactNode => {
        if (typeof child === 'string') {
          return processTimestamps(child);
        }
        if (React.isValidElement(child)) {
          if (child.props.children) {
            const processedChildren = React.Children.map(
              child.props.children,
              processContent
            );
            return React.cloneElement(child, {
              ...child.props,
              children: processedChildren,
            });
          }
          return child;
        }
        return child;
      };

      const processedChildren = React.Children.map(children, processContent);

      return (
        <blockquote {...props} style={blockquoteStyle}>
          <Typography
            component="div"
            sx={{
              margin: 0,
              '& p': { margin: 0 },
              '& strong': {
                fontWeight: 600,
                color: theme.palette.text.primary,
              },
            }}
          >
            {processedChildren}
          </Typography>
        </blockquote>
      );
    },
  };

  const getNotesTitle = () => {
    if (showOriginal || !aiResponse) {
      return 'Recorder Notes';
    }
    return 'Enhanced Notes';
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontSize: '1.25rem',
              fontWeight: 600,
              letterSpacing: '-0.025em',
            }}
          >
            {getNotesTitle()}
          </Typography>
          <IconButton
            onClick={() =>
              showOriginal
                ? setShowRecorderInfoDialog(true)
                : setShowEnhancedInfoDialog(true)
            }
            size="small"
            sx={{
              ml: 0.5,
              width: '24px',
              height: '24px',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <HelpCircle
              sx={{
                fontSize: '20px',
                color: 'text.secondary',
                opacity: 0.7,
              }}
            />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {aiResponse && (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'background.default',
                borderRadius: 2,
                height: '40px',
                display: 'flex',
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiIconButton-root': {
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  margin: '4px',
                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(1px)',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '20px',
                    transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                },
              }}
            >
              <Tooltip
                title={hideQuotes ? 'Show quotes' : 'Hide quotes'}
                placement="top"
                arrow
              >
                <span>
                  <IconButton
                    onClick={() => setHideQuotes(!hideQuotes)}
                    disabled={showOriginal}
                  >
                    {hideQuotes ? <ChevronsDown /> : <ChevronsUp />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Regenerate enhanced notes" placement="top" arrow>
                <IconButton
                  onClick={() => {
                    if (!transcript?.compactSegments?.length) {
                      setShowTranscriptDialog(true);
                      return;
                    }
                    optimizeNotes();
                  }}
                  disabled={showOriginal || isLoading}
                >
                  <RotateCcw />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  showOriginal ? 'Show enhanced notes' : 'Show original notes'
                }
                placement="top"
                arrow
              >
                <IconButton
                  onClick={() => setShowOriginal(!showOriginal)}
                  sx={{
                    color: !showOriginal ? 'primary.main' : 'text.primary',
                  }}
                >
                  {showOriginal ? <Sparkles color="primary" /> : <FileText />}
                </IconButton>
              </Tooltip>
            </Paper>
          )}
          {!aiResponse && (
            <Button
              onClick={() => {
                if (!transcript?.compactSegments?.length) {
                  setShowTranscriptDialog(true);
                  return;
                }
                optimizeNotes();
                setShowOriginal(false);
              }}
              sx={{
                p: 0,
                minWidth: 'auto',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(1px)',
                  },
                }}
              >
                <Sparkles
                  color="primary"
                  sx={{
                    width: 20,
                    height: 20,
                    transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  Enhance with AI
                </Typography>
              </Paper>
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        open={showTranscriptDialog}
        onClose={() => setShowTranscriptDialog(false)}
        aria-labelledby="transcript-dialog-title"
        aria-describedby="transcript-dialog-description"
      >
        <DialogTitle id="transcript-dialog-title">
          Waiting for Transcript
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="transcript-dialog-description">
            The AI enhancement feature will be available once the transcript is
            ready. This usually takes a few minutes after upload.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowTranscriptDialog(false)}
            variant="contained"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRecorderInfoDialog}
        onClose={() => setShowRecorderInfoDialog(false)}
        aria-labelledby="recorder-info-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="recorder-info-dialog-title" sx={{ pb: 1 }}>
          About Recorder Notes
          <IconButton
            aria-label="close"
            onClick={() => setShowRecorderInfoDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 2,
              color: 'text.primary',
              '& p': { mb: 2 },
            }}
          >
            <p>
              Recorder Notes are your original meeting notes, captured in
              real-time. Each note is automatically timestamped and synced with
              your recording, creating a powerful connection between your notes
              and the actual conversation.
            </p>
            <p>
              This timestamp-based synchronization is what makes the AI
              enhancement so powerful later - it can precisely match your notes
              with the surrounding context from the meeting transcript.
            </p>
            <p>
              Focus on writing what&apos;s meaningful to you - key decisions,
              important details, or things you want to follow up on. Don&apos;t
              worry about catching everything.
            </p>
            <p>
              <strong>Quick Tips:</strong>
            </p>
            <ul style={{ marginTop: '-8px' }}>
              <li>
                Click any timestamp to jump to that moment in the recording
              </li>
              <li>
                Write brief notes - they&apos;ll be enhanced later with AI
              </li>
              <li>Don&apos;t worry about formatting or complete sentences</li>
              <li>Focus on capturing key points and decisions</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRecorderInfoDialog(false)}
            variant="contained"
            sx={{ px: 3 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showEnhancedInfoDialog}
        onClose={() => setShowEnhancedInfoDialog(false)}
        aria-labelledby="enhanced-info-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="enhanced-info-dialog-title" sx={{ pb: 1 }}>
          About Enhanced Notes
          <IconButton
            aria-label="close"
            onClick={() => setShowEnhancedInfoDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 2,
              color: 'text.primary',
              '& p': { mb: 2 },
            }}
          >
            <p>
              Enhanced Notes use AI to transform your brief notes into
              comprehensive, well-structured documentation. Because your
              original notes are timestamped, the AI can precisely locate each
              note within the meeting transcript and understand its full
              context.
            </p>
            <p>
              This unique combination of your notes and the transcript allows
              the AI to expand each point with relevant details from the actual
              conversation, while staying focused on what you found important
              enough to write down.
            </p>
            <p>
              <strong>What Gets Enhanced:</strong>
            </p>
            <ul style={{ marginTop: '-8px' }}>
              <li>
                Brief notes are expanded with relevant context from the
                discussion
              </li>
              <li>Key points are organized into clear sections</li>
              <li>Important quotes and timestamps are included</li>
              <li>Action items and decisions are highlighted</li>
              <li>
                Questions are answered with supporting details from the
                transcript
              </li>
              <li>
                Comments and highlights added after the meeting are incorporated
                into the enhanced notes
              </li>
            </ul>
            <p>
              The AI enhancement takes into account not just your original
              notes, but also any comments and highlights you&apos;ve added
              after the meeting. This means you can continue to refine and add
              context to your notes, and the AI will incorporate all of this
              additional information when creating the enhanced version.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowEnhancedInfoDialog(false)}
            variant="contained"
            sx={{ px: 3 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setNoteToDelete(null);
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Note?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            This action cannot be undone. Are you sure you want to delete this
            note?
          </DialogContentText>
          {noteToDelete !== null && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {file?.textData?.notes?.[noteToDelete]?.text}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setNoteToDelete(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteNote}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          Error: {error.message || 'Something went wrong'}
        </Typography>
      )}

      {showOriginal ? (
        <Box>{renderOriginalNotes()}</Box>
      ) : (
        <Box>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={customComponents}
          >
            {aiResponse}
          </ReactMarkdown>
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                mt: 2,
                ml: 2,
              }}
            >
              <img
                src={FadeStaggerCircles}
                alt="Loading..."
                width={40}
                height={40}
                tabIndex={-1}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Notes;
